import { resolveErrors } from 'app/utils/form_errors'
import { fetch, create } from 'app/utils/data'

const initialState = () => ({
  status: null,
  mySubmissions: [],
  publicGallery: [],
  lastErrors: null,
  kitAdditions: []
})

export default {
  namespaced: true,

  state: initialState(),

  getters: {
    status: (state) => state.status,
    isEnabled: (state) => !!(state.status && state.status.enabled),
    contestEnabled: (state) => !!(state.status && state.status.contestEnabled),
    submissionEnabled: (state) => !!(state.status && state.status.submissionEnabled),
    displayWinners: (state) => !!(state.status && state.status.displayWinners),
    submissionCap: (state) => state.status?.submissionCap ?? null,
    contestStartDate: (state) => state.status?.contestStartDate ?? null,
    contestEndDate: (state) => state.status?.contestEndDate ?? null,
    winnerAnnouncementDate: (state) => state.status?.winnerAnnouncementDate ?? null,
    remainingSubmissions: (state) => state.status?.remainingSubmissions ?? null,
    mySubmissions: (state) => state.mySubmissions,
    publicGallery: (state) => state.publicGallery,
    lastErrors: (state) => state.lastErrors,
    kitAdditions: (state) => state.kitAdditions,
    addedSubmissionIds: (state) => [...new Set(state.kitAdditions.map((a) => a.submissionId))]
  },

  mutations: {
    statusFetched(state, status) {
      state.status = status
    },
    kitAdditionsFetched(state, additions) {
      state.kitAdditions = additions
    },
    kitAdditionRecorded(state, { submissionId, kitToken }) {
      const exists = state.kitAdditions.some((a) => a.submissionId === submissionId && a.kitToken === kitToken)
      if (!exists) {
        state.kitAdditions = [...state.kitAdditions, { submissionId, kitToken }]
      }
    },
    mySubmissionsFetched(state, { submissions, remainingSubmissions }) {
      state.mySubmissions = submissions
      if (state.status) {
        state.status = { ...state.status, remainingSubmissions }
      }
    },
    submissionCreated(state, { submission, remainingSubmissions }) {
      state.mySubmissions = [submission, ...state.mySubmissions]
      if (state.status) {
        state.status = { ...state.status, remainingSubmissions }
      }
      state.lastErrors = null
    },
    submissionErrored(state, errors) {
      state.lastErrors = errors
    },
    publicGalleryFetched(state, gallery) {
      state.publicGallery = gallery
    },
    clearErrors(state) {
      state.lastErrors = null
    }
  },

  actions: {
    async fetchStatus({ commit }) {
      try {
        const response = await fetch({ url: '/api/icon-gallery/status', type: 'icon-gallery-status' })
        const attributes = response?.data?.data?.attributes ?? {}
        const status = {
          enabled: !!attributes.enabled,
          contestEnabled: !!attributes['contest-enabled'],
          submissionEnabled: !!attributes['submission-enabled'],
          displayWinners: !!attributes['display-winners'],
          submissionCap: attributes['submission-cap'] ?? null,
          contestStartDate: attributes['contest-start-date'] ?? null,
          contestEndDate: attributes['contest-end-date'] ?? null,
          winnerAnnouncementDate: attributes['winner-announcement-date'] ?? null,
          remainingSubmissions: attributes['remaining-submissions'] ?? null
        }
        commit('statusFetched', status)
        return status
      } catch (e) {
        // Status is read-only and best-effort; fall back to a "disabled" view so the SPA hides Submit-to-Gallery.
        const fallback = {
          enabled: false,
          contestEnabled: false,
          submissionEnabled: false,
          displayWinners: false,
          submissionCap: null,
          contestStartDate: null,
          contestEndDate: null,
          winnerAnnouncementDate: null,
          remainingSubmissions: null
        }
        commit('statusFetched', fallback)
        return fallback
      }
    },

    async fetchMySubmissions({ commit }) {
      const response = await fetch({ url: '/api/icon-gallery/submissions/mine', type: 'icon-gallery-submission' })
      const submissions = (response?.data?.data ?? []).map((row) => ({
        id: row.id,
        name: row.attributes.name,
        status: row.attributes.status,
        insertedAt: row.attributes['inserted-at']
      }))
      const remainingSubmissions = response?.data?.meta?.['remaining-submissions'] ?? null
      commit('mySubmissionsFetched', { submissions, remainingSubmissions })
      return submissions
    },

    async submit({ commit }, { name, width, height, path, pathSecondary, socialPlatform, socialHandle }) {
      try {
        commit('clearErrors')
        const data = { name, width, height, path }
        // Omitted for a single tone: the changeset takes a null but rejects a
        // blank, so absent has to mean absent.
        if (pathSecondary) data.pathSecondary = pathSecondary
        if (socialHandle) {
          data.socialHandle = socialHandle
          if (socialPlatform) data.socialPlatform = socialPlatform
        }
        const response = await create({
          url: '/api/icon-gallery/submissions',
          type: 'icon-gallery-submission',
          data
        })
        const row = response?.data?.data
        const submission = {
          id: row.id,
          name: row.attributes.name,
          status: row.attributes.status,
          insertedAt: row.attributes['inserted-at']
        }
        const remainingSubmissions = response?.data?.meta?.['remaining-submissions'] ?? null
        commit('submissionCreated', { submission, remainingSubmissions })
        return submission
      } catch (e) {
        const formErrors = resolveErrors(e)
        commit('submissionErrored', formErrors)
        throw formErrors
      }
    },

    async fetchPublicGallery({ commit }) {
      const response = await fetch({
        url: '/api/icon-gallery',
        type: 'icon-gallery-submission'
      })
      const rows = response?.data?.data ?? []
      const gallery = rows.map((row) => ({
        id: row.id,
        name: row.attributes.name,
        width: row.attributes.width,
        height: row.attributes.height,
        path: row.attributes.path,
        pathSecondary: row.attributes['path-secondary'] ?? null,
        highlighted: !!row.attributes.highlighted,
        winner: !!row.attributes.winner,
        winnerCategory: row.attributes['winner-category'] ?? null,
        socialPlatform: row.attributes['social-platform'] ?? null,
        socialHandle: row.attributes['social-handle'] ?? null
      }))
      commit('publicGalleryFetched', gallery)
      return gallery
    },

    async addToKit({ commit }, { submissionId, kitToken, name }) {
      try {
        const response = await create({
          url: `/api/clouds/kits/${kitToken}/icon-uploads/from-gallery/${submissionId}`,
          type: 'icon-upload',
          data: { name }
        })
        commit('kitAdditionRecorded', { submissionId: String(submissionId), kitToken })
        return response?.data?.data
      } catch (e) {
        throw resolveErrors(e)
      }
    },

    // Best-effort: backs the "already in your Kit" indicator, so a signed-out
    // caller or transient failure just leaves the indicator off.
    async fetchMyKitAdditions({ commit }) {
      try {
        const response = await fetch({ url: '/api/icon-gallery/kit-additions/mine', type: 'icon-gallery-kit-additions' })
        const additions = (response?.data?.data?.attributes?.additions ?? []).map((a) => ({
          submissionId: String(a['submission-id']),
          kitToken: a['kit-token']
        }))
        commit('kitAdditionsFetched', additions)
      } catch (e) {
        // indicator stays off
      }
    }
  }
}
